import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import learningDocService from "../../services/learningDocService";
import LoadingSpinner from "../common/LoadingSpinner";

function AdminLearningDocumentation() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const handleDownload = async (file) => {
    try {
      if (!file || !file.id) return;
      await learningDocService.downloadAdminFile(file.id, file.fileName);
    } catch (err) {
      console.error("Failed to download file:", err);
      // We could add a toast notification here
      alert("Failed to download file. Please try again.");
    }
  };

  useEffect(() => {
    const fetchLearningDocumentation = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await learningDocService.getAdminLearningDocumentation(userId);
        setData(result);
      } catch (err) {
        console.error("Error fetching learning documentation:", err);
        setError("Failed to load learning documentation. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchLearningDocumentation();
    }
  }, [userId]);

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-CH", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-green-cloud flex items-center justify-center">
        <LoadingSpinner message="Loading learning documentation..." size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-green-cloud">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-4">
            <button
              onClick={() => navigate("/students")}
              className="btn-brand-outline"
            >
              <i className="fas fa-arrow-left mr-2"></i>
              Back to Students
            </button>
          </div>

          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl shadow-brand">
            <div className="flex items-start">
              <i className="fas fa-exclamation-triangle text-red-500 mr-3 mt-0.5"></i>
              <div>
                <p className="font-semibold mb-1">Error Loading Documentation</p>
                <p className="text-sm">{error || "Student not found."}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const userInitial = data.user.name?.charAt(0) || "?";

  return (
    <div className="min-h-screen bg-green-cloud">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate("/students")}
            className="mb-4 flex items-center text-sunrise-red hover:text-sunrise-600 transition-colors"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to User List
          </button>

          <div className="card-brand-elevated p-8 bg-gradient-to-r from-ice-white to-pale-dawn/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-center">
                <div className="w-16 h-16 bg-gradient-to-br from-sunrise-red to-sunrise-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mr-4">
                  {userInitial}
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-grey-dusk flex items-center mb-1">
                    <i className="fas fa-book text-sunrise-red mr-3"></i>
                    Learning Documentation
                  </h1>
                  <p className="text-lg text-slate-cloud">
                    {data.user.name} (@{data.user.username})
                  </p>
                  <p className="text-sm text-gray-500">{data.user.email}</p>
                </div>
              </div>
              <div className="text-sm text-slate-cloud md:text-right">
                <p>Read-only access to this learner&apos;s documentation.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Link Card */}
        <div className="bg-ice-white rounded-xl shadow-brand border border-pale-dawn p-6 mb-6">
          <h2 className="text-xl font-bold text-grey-dusk mb-4 flex items-center">
            <i className="fas fa-link text-sunrise-red mr-3"></i>
            Learning Documentation Links
          </h2>

          {(data.learningDocumentationUrl1 || data.learningDocumentationUrl2 || data.learningDocumentationUrl3) ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-gray-700 mb-3 font-medium">
                External Documentation:
              </p>
              <div className="space-y-2">
                {data.learningDocumentationUrl1 && (
                  <a
                    href={data.learningDocumentationUrl1}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-800 underline break-all flex items-center text-sm"
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    {data.learningDocumentationAlias1 || data.learningDocumentationUrl1}
                  </a>
                )}
                {data.learningDocumentationUrl2 && (
                  <a
                    href={data.learningDocumentationUrl2}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-800 underline break-all flex items-center text-sm"
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    {data.learningDocumentationAlias2 || data.learningDocumentationUrl2}
                  </a>
                )}
                {data.learningDocumentationUrl3 && (
                  <a
                    href={data.learningDocumentationUrl3}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-red-600 hover:text-red-800 underline break-all flex items-center text-sm"
                  >
                    <i className="fas fa-external-link-alt mr-2"></i>
                    {data.learningDocumentationAlias3 || data.learningDocumentationUrl3}
                  </a>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <i className="fas fa-link text-4xl mb-3"></i>
              <p className="text-sm">
                No learning documentation links provided by this learner yet.
              </p>
            </div>
          )}
        </div>

        {/* Files Card */}
        <div className="bg-ice-white rounded-xl shadow-brand border border-pale-dawn p-6">
          <h2 className="text-xl font-bold text-grey-dusk mb-4 flex items-center">
            <i className="fas fa-file-alt text-sunrise-red mr-3"></i>
            Learning Documentation Files
          </h2>

          {data.files && data.files.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      File Name
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Size
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Upload Date
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.files.map((file) => (
                    <tr key={file.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <i
                            className={`fas ${
                              file.mimeType === "application/pdf"
                                ? "fa-file-pdf text-red-500"
                                : "fa-file-image text-red-500"
                            } mr-2 text-lg`}
                          ></i>
                          <span className="text-sm font-medium text-gray-900">
                            {file.fileName}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                          {file.mimeType === "application/pdf"
                            ? "PDF"
                            : file.mimeType.startsWith("image/")
                            ? "Image"
                            : "File"}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatFileSize(file.sizeBytes)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(file.createdAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleDownload(file)}
                          className="text-red-600 hover:text-red-900 inline-flex items-center bg-transparent border-0 cursor-pointer"
                        >
                          <i className="fas fa-download mr-1"></i>
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-400">
              <i className="fas fa-folder-open text-5xl mb-4"></i>
              <p className="text-sm">
                No learning documentation files uploaded by this learner yet.
              </p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

export default AdminLearningDocumentation;
